/**
 * Earthworks EW-5 (fieldiq-api#1290): field tickets and change orders — who
 * may press what, and the status skins.
 *
 * These decide which buttons are DRAWN. The API decides what happens; a
 * refusal that still arrives is shown as a refusal (ewRefusal), never as
 * success. Each rule below mirrors one rule in fieldiq-api lib/ew-tickets.ts,
 * lib/ew-change-orders.ts or the routes' role gates, and is named for it.
 */
import { EW_ADMIN_ROLES, EW_RATE_READ_ROLES, EW_UNKNOWN_SKIN, type EwSkin } from './earthworks';

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);

/** POST /tickets/:id/approve is EW_ADMIN: admin and office staff, nobody else. */
export const canApproveEwTicket = inList(EW_ADMIN_ROLES);

/** The office ticket and change-order routes (reads and writes) are EW_RATE_READ: prices are not a field_worker's. */
export const canWorkEwTickets = inList(EW_RATE_READ_ROLES);

/** A field_worker's field ticket arrives without prices; no price column is drawn for them at all. */
export const canSeeEwTicketPrices = inList(EW_RATE_READ_ROLES);

/** Lines, rebuild and the paper sign-off change only while the ticket is a draft. */
export const ewTicketEditable = (status: string | null | undefined) => status === 'draft';

/** Approval needs the customer's signature first. */
export const ewTicketApprovable = (status: string | null | undefined) => status === 'signed';

/** Void: not already void, and not on an invoice (void the invoice first). */
export const ewTicketVoidable = (t: { status: string; invoicedAt: string | null } | null | undefined) =>
  !!t && t.status !== 'void' && !t.invoicedAt;

/** A phone signature needs both a typed name and a confirmed signature. */
export const ewCanSubmitSignature = (name: string, signature: string | null | undefined) =>
  name.trim() !== '' && !!signature;

/** Change-order lines change while draft or sent (an edit of a sent one returns it to draft). */
export const ewCoEditable = (status: string | null | undefined) => status === 'draft' || status === 'sent';
export const ewCoSendable = (status: string | null | undefined) => status === 'draft' || status === 'sent';
export const ewCoVoidable = (status: string | null | undefined) =>
  status === 'draft' || status === 'sent' || status === 'declined';
/** The crew can take the customer's signature on the phone while draft or sent. */
export const ewCoSignable = (status: string | null | undefined) => status === 'draft' || status === 'sent';

export const EW_TICKET_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'signed', label: 'Signed' },
  { value: 'approved', label: 'Approved' },
  { value: 'void', label: 'Void' },
] as const;

export const EW_CO_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'void', label: 'Void' },
] as const;

const TICKET_SKIN: Record<string, EwSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  signed: { label: 'Signed', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  void: { label: 'Void', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const ewTicketSkin = (status: string | null | undefined): EwSkin =>
  status && Object.prototype.hasOwnProperty.call(TICKET_SKIN, status) ? TICKET_SKIN[status] : EW_UNKNOWN_SKIN;

// Sent and draft both wait on someone; declined and void are both "not this one".
const CO_SKIN: Record<string, EwSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  sent: { label: 'Sent', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  declined: { label: 'Declined', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  void: { label: 'Void', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const ewCoSkin = (status: string | null | undefined): EwSkin =>
  status && Object.prototype.hasOwnProperty.call(CO_SKIN, status) ? CO_SKIN[status] : EW_UNKNOWN_SKIN;

export const EW_LINE_SOURCE_LABEL: Record<string, string> = {
  auto: 'From logs',
  change_order: 'Change order',
  manual: 'Added',
};
export const ewLineSource = (s: string | null | undefined) => (s && EW_LINE_SOURCE_LABEL[s]) || '—';

export const EW_FILE_KIND_LABEL: Record<string, string> = {
  paper_signed: 'Signed paper ticket',
  signed_copy: 'Customer-signed copy',
};
export const ewFileKind = (k: string | null | undefined) => (k && EW_FILE_KIND_LABEL[k]) || '—';

/** How a ticket was signed, as words. */
export const ewSignedVia = (v: string | null | undefined) =>
  v === 'phone' ? 'On the phone' : v === 'paper' ? 'Paper ticket photo' : v === 'link' ? 'Customer link' : '—';
